import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { publicFetch } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface Banner {
  id: number;
  title: string;
  subtitle: string;
  imageUrl: string;
  bannerType: string;
  placement: string;
  actionType: string;
  actionPayload: string;
  priority: number;
}

const LandingBanners = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await publicFetch('/banners?placement=LANDING');
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          const sortedBanners = data.sort((a, b) => b.priority - a.priority);
          setBanners(sortedBanners);
          setCurrentIndex(0);
        } else {
          setBanners([]);
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error);
        setBanners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [banners.length]);

  // Clear interval on manual navigation
  const clearAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Restart auto-scroll
  const restartAutoScroll = useCallback(() => {
    clearAutoScroll();
    if (banners.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
    }
  }, [banners.length, clearAutoScroll]);

  const handleImageError = useCallback((bannerId: number) => {
    setImageErrors(prev => new Set(prev).add(bannerId));
  }, []);

  const handleBannerClick = useCallback((banner: Banner) => {
    try {
      if (banner.actionType === 'FILTER' && banner.actionPayload) {
        const payload = JSON.parse(banner.actionPayload);
        
        // Build URL params for AllProductsPage
        const searchParams = new URLSearchParams();
        if (payload.category) searchParams.set('category', payload.category);
        if (payload.subCategory) searchParams.set('subCategory', payload.subCategory);
        if (payload.sort) searchParams.set('sort', payload.sort);
        
        navigate(`/products?${searchParams.toString()}`);
      }
    } catch (e) {
      console.error('Invalid banner payload:', e);
      // Fallback to products page
      navigate('/products');
    }
  }, [navigate]);

  const goToSlide = useCallback((index: number) => {
    clearAutoScroll();
    setCurrentIndex(index);
    setTimeout(restartAutoScroll, 3000); // Restart auto-scroll after 3 seconds
  }, [clearAutoScroll, restartAutoScroll]);

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  }, [currentIndex, banners.length, goToSlide]);

  const goToNext = useCallback(() => {
    const newIndex = (currentIndex + 1) % banners.length;
    goToSlide(newIndex);
  }, [currentIndex, banners.length, goToSlide]);

  if (loading) {
    return (
      <div className="w-full mb-8">
        <Skeleton className="w-full h-48 sm:h-64 md:h-80 rounded-2xl" />
        <div className="flex justify-center mt-4 gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="w-2 h-2 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!banners.length) {
    return null;
  }

  const validBanners = banners.filter(b => !imageErrors.has(b.id));

  if (!validBanners.length) {
    return (
      <div className="w-full mb-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl text-center">
        <p className="text-amber-800 text-sm">Banners temporarily unavailable</p>
      </div>
    );
  }

  return (
    <div className="w-full mb-8">
      <div className="relative overflow-hidden rounded-2xl shadow-xl group">
        {/* Banner Container */}
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {validBanners.map((banner) => (
            <div
              key={banner.id}
              className="w-full flex-shrink-0 relative cursor-pointer group"
              onClick={() => handleBannerClick(banner)}
            >
              <div className="relative h-48 sm:h-64 md:h-80 overflow-hidden">
                <img
                  src={banner.imageUrl}
                  alt={banner.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={() => handleImageError(banner.id)}
                  loading="lazy"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Text Overlay */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-0 left-0 right-0 p-6 text-white"
                >
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 line-clamp-2">
                    {banner.title}
                  </h3>
                  <p className="text-white/90 text-sm md:text-base mb-4 line-clamp-1">
                    {banner.subtitle}
                  </p>
                  <Button 
                    size="sm"
                    className="bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30 transition-all"
                  >
                    Shop Now
                  </Button>
                </motion.div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        {validBanners.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 opacity-0 md:opacity-100 group-hover:opacity-100"
              aria-label="Previous banner"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 opacity-0 md:opacity-100 group-hover:opacity-100"
              aria-label="Next banner"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Pagination Dots */}
      {validBanners.length > 1 && (
        <div className="flex justify-center mt-4 gap-2">
          {validBanners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? 'bg-primary w-6' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LandingBanners;